import { Box, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

interface Load {
  width: number;
  height: number;
  depth: number;
  x: number;
  y: number;
  z: number;
  color: number;
}

interface Container {
  width: number;
  height: number;
  depth: number;
}

interface Props {
  loads: Load[];
  container: Container;
}

const isContainerValid = (container: Container | undefined) =>
  container && container.width > 0 && container.height > 0 && container.depth > 0;

const isLoadValid = (load: Load, container: Container) =>
  load &&
  load.width > 0 &&
  load.height > 0 &&
  load.depth > 0 &&
  load.x >= 0 &&
  load.y >= 0 &&
  load.z >= 0 &&
  load.x + load.width <= container.width &&
  load.y + load.height <= container.height &&
  load.z + load.depth <= container.depth;

const Custom3dLoad = ({ loads, container }: Props) => {
  const mountRef = useRef<HTMLDivElement>(null);

  const isDataValid =
    isContainerValid(container) &&
    Array.isArray(loads) &&
    loads.length > 0 &&
    loads.every(load => isLoadValid(load, container));

  useEffect(() => {
    if (!isDataValid || !mountRef.current) return;

    const mount = mountRef.current;
    const width = mount.clientWidth;
    const height = mount.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#fff');

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    mount.appendChild(renderer.domElement);

    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 10000);
    camera.position.set(
      container.width * 1.5,
      container.height * 1.2,
      container.depth * 1.5
    );
    camera.lookAt(container.width / 2, container.height / 2, container.depth / 2);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(container.width / 2, container.height / 2, container.depth / 2);
    controls.update();

    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(20, 20, 20);
    scene.add(directionalLight);

    const containerBox = new THREE.BoxGeometry(
      container.width,
      container.height,
      container.depth
    );
    const edges = new THREE.EdgesGeometry(containerBox);
    const line = new THREE.LineSegments(
      edges,
      new THREE.LineBasicMaterial({ color: 0x000000 })
    );
    line.position.set(container.width / 2, container.height / 2, container.depth / 2);
    scene.add(line);

    loads.forEach(load => {
      const geometry = new THREE.BoxGeometry(load.width, load.height, load.depth);
      const material = new THREE.MeshLambertMaterial({
        color: load.color,
        transparent: true,
        opacity: 0.8
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(
        load.x + load.width / 2,
        load.y + load.height / 2,
        load.z + load.depth / 2
      );
      scene.add(mesh);

      const wireframe = new THREE.WireframeGeometry(geometry);
      const outline = new THREE.LineSegments(
        wireframe,
        new THREE.LineBasicMaterial({ color: 0x000000 })
      );
      outline.position.copy(mesh.position);
      scene.add(outline);
    });

    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      mount.removeChild(renderer.domElement);
      renderer.dispose();
      controls.dispose();
    };
  }, [isDataValid, loads, container]);

  if (!isDataValid) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          py: 6,
          px: 3,
          color: 'text.secondary',
          textAlign: 'center',
          fontSize: 12,
          userSelect: 'none'
        }}
      >
        <Typography variant='body2' fontWeight={500} mb={0.5} color={'#00000099'}>
          No data found to display
        </Typography>
      </Box>
    );
  }

  return <Box ref={mountRef} sx={{ width: '100%', height: '100%' }} />;
};

export default Custom3dLoad;
